import {
  ButtonVariant,
  defaultTransitionClass,
  disabledClass,
  nToPx,
  themeVars,
  toBorder,
} from '@tundr/theme';
import { style, styleVariants } from '@vanilla-extract/css';
import {
  ButtonAlignmentVariant,
  ButtonSizes,
  ButtonWidthVariant,
} from './Button.types';

export const baseButton = style([
  defaultTransitionClass,
  {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    fontWeight: themeVars.font.weight.bold,
  },
]);

export const btnVariants = styleVariants({
  [ButtonVariant.primary]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.primary].text.default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.primary].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          backgroundColor:
            themeVars.colors.components.button[ButtonVariant.primary].background
              .hover,
        },
      },
    },
  ],
  [ButtonVariant.secondary]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.secondary].text
          .default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.secondary].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          backgroundColor:
            themeVars.colors.components.button[ButtonVariant.secondary]
              .background.hover,
        },
      },
    },
  ],
  [ButtonVariant.tertiary]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.tertiary].text.default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.tertiary].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          backgroundColor:
            themeVars.colors.components.button[ButtonVariant.tertiary]
              .background.hover,
        },
      },
    },
  ],
  [ButtonVariant.transparent]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.transparent].text
          .default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.transparent].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          color:
            themeVars.colors.components.button[ButtonVariant.transparent].text
              .hover,
        },
      },
    },
  ],
  [ButtonVariant.transparentOutlined]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.transparentOutlined]
          .text.default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.transparentOutlined]
          .background.default,
      border: toBorder(
        themeVars.colors.components.button[ButtonVariant.transparentOutlined]
          .border,
      ),

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          color:
            themeVars.colors.components.button[
              ButtonVariant.transparentOutlined
            ].text.hover,
        },
      },
    },
  ],
  [ButtonVariant.transparentUnderlined]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.transparentOutlined]
          .text.default,
      textDecoration: 'underline',
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.transparentOutlined]
          .background.default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          color:
            themeVars.colors.components.button[
              ButtonVariant.transparentOutlined
            ].text.hover,
        },
      },
    },
  ],
  [ButtonVariant.error]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.error].text.default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.error].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          backgroundColor:
            themeVars.colors.components.button[ButtonVariant.error].background
              .hover,
        },
      },
    },
  ],
  [ButtonVariant.success]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.success].text.default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.success].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          backgroundColor:
            themeVars.colors.components.button[ButtonVariant.success].background
              .hover,
        },
      },
    },
  ],
  [ButtonVariant.iconButton]: [
    baseButton,
    {
      color:
        themeVars.colors.components.button[ButtonVariant.iconButton].text
          .default,
      backgroundColor:
        themeVars.colors.components.button[ButtonVariant.iconButton].background
          .default,

      selectors: {
        [`&:hover&:not(${disabledClass})`]: {
          backgroundColor:
            themeVars.colors.components.button[ButtonVariant.iconButton]
              .background.hover,
        },
      },
    },
  ],
});

export const btnWidth = styleVariants({
  [ButtonWidthVariant.AUTO]: {
    width: 'auto',
    minWidth: 145,
    maxWidth: 280,
  },
  [ButtonWidthVariant.FULL]: {
    width: '100%',
    maxWidth: '100%',
  },
  [ButtonWidthVariant.FIXED]: {
    width: 180,
  },
  [ButtonWidthVariant.MIN]: {
    width: 'auto',
    minWidth: 48,
    maxWidth: 280,
  },
});

export const btnIconRight = style({
  marginLeft: themeVars.space.x2,
});

export const btnIconLeft = style({
  marginRight: themeVars.space.x2,
});

export const btnAlignment = styleVariants({
  [ButtonAlignmentVariant.LEFT]: {
    justifyContent: 'left',
  },
  [ButtonAlignmentVariant.CENTER]: {
    justifyContent: 'center',
  },
  [ButtonAlignmentVariant.RIGHT]: {
    justifyContent: 'right',
  },
});

export const btnSize = styleVariants({
  [ButtonSizes.NO_PADDING]: {
    minHeight: 38,
    height: 38,
    fontSize: themeVars.font.size.textMd,
    padding: 0,
  },
  [ButtonSizes.SMALL]: {
    minHeight: 38,
    height: 38,
    fontSize: `${themeVars.font.size.textSm}!important`,
    padding: nToPx(themeVars.space.x2, themeVars.space.x4),
    borderRadius: themeVars.radius.sm,
  },
  [ButtonSizes.DEFAULT]: {
    minHeight: 48,
    height: 48,
    fontSize: themeVars.font.size.textMd,
    padding: nToPx(themeVars.space.x2, themeVars.space.x6),
    borderRadius: themeVars.radius.sm,
  },
});

export const buttonWithoutEllipsisClass = style({
  minHeight: 38,
  height: 'auto',
});
